import { Box, Typography, Chip, Divider, CircularProgress } from "@mui/material";
import { getSentimentMeta, TimelineItem, TimelineTooltipPayload, validateDate } from "../../utils/utils";
import SelectedAsk7GIcon from "../../assets/common/ask7g-selected.svg";
import UserInteractionsService from "../../services/interactions/Interactions";
import { useContext, useEffect, useState } from "react";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import PositiveIcon from "../../assets/accounts/positivePlus.svg";
import NeutralIcon from "../../assets/accounts/neutralPlus.svg";
import NegativeIcon from "../../assets/accounts/negativeMinus.svg";
import "../../styles/account.css";
import InteractionIcon from "../../assets/accounts/interactioncolor.svg";
import GroupInteractionItem from "./GroupInteractionItem";
import { useNavigate } from "react-router-dom";
import { GlobalContext } from "../../context/GlobalContext";

interface CallQualitySection {
  score: string;
}
interface CallQualityData {
  opening?: CallQualitySection;
  execution?: CallQualitySection;
  closing?: CallQualitySection;
}


const InteractionTooltip = ({
  mode,
  interactions,
  navigationState,
  groupDateKey,
  anchorInteractionId,
}: TimelineTooltipPayload) => {
  const [showSpinner, setShowSpinner] = useState(false);
  const { showSnackbar } = useDisplaySnackbar();
  const navigate = useNavigate();
  const [insightData, setInsightData] = useState<{
    overallSentiment?: string;
    summary?: string;
  } | null>(null);
  const [callQualityData, setCallQualityData] = useState<CallQualityData | any>(null);
  const [activeInteraction, setActiveInteraction] = useState<TimelineItem | null>(null);
  const resolvedParentPageIdentifier =
    navigationState?.parent_page_identifier ||
    (navigationState?.slideOverParent === "client-interaction-overview"
      ? "client-interaction-overview"
      : "account-customer");
    const { impersonationDetails } = useContext(GlobalContext);
    const impersonatingUserPorId = impersonationDetails?.impersonatingUserPorId;
    const accessLevel = impersonationDetails?.accessType;

  useEffect(() => {
    if (interactions.length) {
      setActiveInteraction(interactions[0]);
    }
  }, [interactions]);
  const interactionId = activeInteraction?.id;

  const { isSuccess: isInsightsSuccess, isLoading: isInsightsLoading, data: insightsData } =
    UserInteractionsService.useGetConversationInsights(
      {
        interactionId: interactionId || "",
        impersonatingPorId: impersonatingUserPorId,
        accessLevel: accessLevel,
      },
      !!interactionId
    );
  
    useEffect(() => {
      setShowSpinner(isInsightsLoading);
      if (isInsightsSuccess && insightsData) {
        const response = insightsData?.data;
        const sentiment = response?.[0]?.sentiment;
        if (sentiment) {
          setInsightData({
            overallSentiment: sentiment.overallSentiment,
            summary: sentiment.sentimentSummary,
          });
        }
      }
    }, [isInsightsSuccess, insightsData]);

  const {
    isLoading: isAbstractionSummaryInsightsLoading,
    isFetching:  isAbstractionSummaryInsightsFetching,
    isSuccess: isAbstractionSummaryInsightsSuccess,
    isError: isAbstractionSummaryInsightsError,
    error: AbstractionSummaryInsightsError,
    data: abstractionSummaryInsightsData,
  } = UserInteractionsService.useGetAbstractiveInsights({
    interactionId: interactionId || "",
    impersonatingPorId: impersonatingUserPorId,
    accessLevel: accessLevel,
  },
    !!interactionId
  ) 
  useEffect(()=>{
      setShowSpinner(isAbstractionSummaryInsightsFetching || isAbstractionSummaryInsightsLoading)
      if(isAbstractionSummaryInsightsSuccess){
        const payload = abstractionSummaryInsightsData?.data?.payload;
      if (payload && typeof payload === "object" && Object.keys(payload).length > 0) {
      const roleKey = payload ? Object.keys(payload)[0] : null;
      const insights = roleKey ? payload[roleKey] : null;
  
      if (insights && typeof insights === "object") {
        setCallQualityData(insights);
        return;
      } 
    }
    }
      if(isAbstractionSummaryInsightsError){
        const errorMessage = AbstractionSummaryInsightsError?.response?.data?.errors || "An unknown error occurred";
        showSnackbar("error", errorMessage);
      }
    },[abstractionSummaryInsightsData,
      isAbstractionSummaryInsightsFetching,
      isAbstractionSummaryInsightsLoading,
      isAbstractionSummaryInsightsSuccess,
      isAbstractionSummaryInsightsError,
    ])

    useEffect(() => {
      setInsightData(null);
      setCallQualityData(null);
      setShowSpinner(true);
    }, [interactions]);

    const getSentimentIcon = (sentiment?: string) => {
      if (sentiment === "Positive") {
        return (
          <img
            src={PositiveIcon}
            alt="Positive"
            width={12}
            height={12}
          />
        );
      }

      if (sentiment === "Negative") {
        return (
          <img
            src={NegativeIcon}
            alt="Negative"
            width={12}
            height={12}
          />
        );
      }

      if (sentiment === "Neutral") {
        return (
          <img
            src={NeutralIcon}
            alt="Neutral"
            width={12}
            height={12}
          />
        );
      }
    };

    const normalizeKey = (key: string) => {
      if (key.includes("opening")) return "opening";
      if (key.includes("execution")) return "execution";
      if (key.includes("closing")) return "closing";
      return key;
    };

    const callMetrics = ["opening", "execution", "closing"].map((key) => {
      const dataKey = Object.keys(callQualityData || {}).find(k => normalizeKey(k) === key);
      const score = dataKey ? callQualityData[dataKey]?.score : undefined;
      const sentiment = getSentimentMeta(score);

      return {
        key,
        label: `Call ${key.charAt(0).toUpperCase() + key.slice(1)}`,
        sentiment,
      };
    }).filter(item => item.sentiment);


    const handleInteractionClick = () => {
      if (!activeInteraction) return;

      navigate(`/meeting/${activeInteraction.id}`, {
        state: {
          parent_page_identifier: resolvedParentPageIdentifier,
          restoreTooltip: true,
          restoreTooltipInSlideOver: Boolean(navigationState?.slideOverParent),
          interactionId: activeInteraction.id,
          tooltipGroupDateKey: groupDateKey,
          tooltipAnchorInteractionId: anchorInteractionId,
          tooltipPayload: {
            mode,
            interactions,
            emailIds: mode === "group" ? undefined : undefined,
            groupDateKey,
            anchorInteractionId,
          },
          ...navigationState,
        },
      });
    };

  return (
    <>
    <Box className="interaction-tooltip">
        {showSpinner && (
          <Box className="interaction-tooltip__spinner">
            <CircularProgress size={24} />
          </Box>
        )}
      <Box className="interaction-tooltip__header">
        {mode === "group" ? (
          <>
            <Typography variant="h5">
              {interactions.length} Interactions
            </Typography>
            <Typography className="interaction-tooltip__date">
              {validateDate(interactions[0].start_time, "DD MMM, YYYY")}
            </Typography>
          </>
        ) : (
          <>
            <Box className="interaction-tooltip__header-row clickable">
              <img
                src={InteractionIcon}
                className="interaction-icon"
                width={16}
              />
              <Typography
                variant="h5"
                className="interaction-tooltip_title"
                onClick={handleInteractionClick}
              >
                {activeInteraction?.title || "Interaction"}
              </Typography>
            </Box>
            <Typography className="interaction-tooltip__date">
              {validateDate(activeInteraction?.start_time, "DD MMM, YYYY")}
            </Typography>
          </>
        )}
      </Box>

      <Divider className="interaction-tooltip__divider" />
      {mode === "group" && (
        <Box className="interaction-tooltip__list">
          {interactions.map((interaction, index) => (
            <GroupInteractionItem
              key={interaction.id}
              interaction={interaction}
              isLast={index === interactions.length - 1}
              navigationState={navigationState}
              groupDateKey={groupDateKey}
              anchorInteractionId={anchorInteractionId}
              allInteractions={interactions}
            />
          ))}
        </Box>
      )}

      {/* Insight */}
      {mode !== "group" && (
      <>
      <Box className="interaction-tooltip__insight">
        <Box className="interaction-tooltip__insight-title">
          <img
            src={SelectedAsk7GIcon}
            className="interaction-tooltip__insight-icon"
          />
          <Typography variant="h1" fontWeight={600} color={"#B902CA"}>
            Insight
          </Typography>
          {insightData?.overallSentiment && 
          <Chip
            icon={getSentimentIcon(insightData?.overallSentiment)}
            label={insightData?.overallSentiment}
            size="small"
            sx={{
              height: 20,
              border: "1px solid #E0E0E0",
              borderRadius: "7px",
              backgroundColor: "#FBFBFB",
              color:
                insightData?.overallSentiment === "Positive"
                  ? "#158F4C"
                  : insightData?.overallSentiment === "Negative"
                  ? "#B42318"
                  : insightData?.overallSentiment === "Neutral"
                  ? "#DF9621"
                  :"",
              fontWeight: 600,
              fontSize: "11px"
            }}
          />
          }
        </Box>
        <Box className="interaction-tooltip__insight-text">
        <Typography className="tooltip-insight">
          {insightData?.summary ||
            "No Recording Available."}
        </Typography>
        </Box>
      </Box>

      {/* Footer metrics */}
      <Box className="interaction-tooltip__metrics">
        {callMetrics.map((item) => (
          <Box key={item.key} className="interaction-tooltip__metric-row">
            <img
              src={SelectedAsk7GIcon}
              className="interaction-tooltip__metric-icon"
            />
            <Typography variant="h5" color="#B902CA">
              {item.label}:{" "}
              <span
                className={`sentiment-${item.sentiment!.label.toLowerCase()}`}
              >
                {item.sentiment!.label}
              </span>
            </Typography>
          </Box>
        ))}
      </Box>
      </>
      )}
    </Box>
    </>
  );
};

export default InteractionTooltip;
